import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { ArrowRightIcon, CheckIcon, PhoneIcon } from "lucide-react";
import { useSeo, faqSchema } from "../hooks/useSeo";
import { PageHero } from "../components/ui/PageHero";
import { CtaLink } from "../components/ui/CtaLink";
import { CtaBand } from "../components/ui/CtaBand";
import { Reveal } from "../components/ui/Reveal";
import { FaqAccordion } from "../components/ui/FaqAccordion";
import { SectionHeading } from "../components/ui/SectionHeading";
import { ProcessTimeline } from "../components/process/ProcessTimeline";
import { getService } from "../data/services";
import { CONTACT, PRICING, SITE } from "../data/site";
import { NotFound } from "./NotFound";
export function ServiceDetail() {
  const {
    slug
  } = useParams();
  const service = getService(slug);
  const schema = useMemo(() => {
    if (!service) return undefined;
    return [{
      "@context": "https://schema.org",
      "@type": "Service",
      name: service.name,
      description: service.intro,
      provider: {
        "@type": "LocalBusiness",
        name: SITE.name,
        email: CONTACT.email
      },
      url: `${SITE.url}/services/${service.slug}`
    }, faqSchema(service.faqs)];
  }, [service]);
  useSeo({
    title: service?.name ?? "Service not found",
    description: service?.intro ?? "This service page could not be found.",
    path: `/services/${slug ?? ""}`,
    schema,
    noindex: !service
  });
  if (!service) return <NotFound data-id="element-906" data-mp="uhqhp4drf5c0" />;
  const isOngoing = service.slug === "hosting-maintenance";
  return <>
      <PageHero crumbs={[{
      label: "Services",
      href: "/services"
    }, {
      label: service.name
    }]} eyebrow={service.category} title={service.headline} lead={service.intro} actions={<>
            <CtaLink to="/contact" track={`service_${service.slug}_build`} size="lg" data-id="element-908" data-mp="6s5zinpfkc3s">Build My Website <ArrowRightIcon className="h-4 w-4" data-id="element-909" data-mp="bpfo614zwjqk" /></CtaLink>
            <CtaLink to={CONTACT.phones[0].href} variant="ghost" track={`service_${service.slug}_call`} size="lg" data-id="element-910" data-mp="2dfmvz0k36gan"><PhoneIcon className="h-4 w-4" data-id="element-911" data-mp="28pg7d0pkqvse" />Call us</CtaLink>
          </>} aside={<div className="rounded-[clamp(1rem,1.5vw,1.75rem)] border border-line bg-ink-2/80 p-[clamp(1.5rem,2.4vw,2.75rem)] backdrop-blur" data-id="element-912" data-mp="23pts6lq6r5gy">
            <p className="t-eyebrow text-muted" data-id="element-913" data-mp="1zq2xi6xjg575">{isOngoing ? PRICING.hosting.name : "Pricing"}</p>
            <p className="mt-4 font-display text-[clamp(2.5rem,1.8rem+2.4vw,5rem)] font-semibold leading-none tracking-tight" data-id="element-914" data-mp="2we8vwc3va1cz">
              {isOngoing ? <>{PRICING.hosting.price}<span className="text-xl text-fg-2" data-id="element-915" data-mp="2ndkqu6wgbbc8">{PRICING.hosting.cadence}</span></> : service.category === "Websites" ? <>From {PRICING.build.price}</> : "Quoted upfront"}
            </p>
            <p className="mt-4 text-muted" data-id="element-916" data-mp="1bynsu7iahbsq">
              {isOngoing ? "Month-to-month. Cancel anytime." : "Your exact scope and price are confirmed in writing before any work begins."}
            </p>
          </div>} data-id="element-907" data-mp="fsud5hawwag5" />

      <section className="shell section grid gap-[clamp(3rem,5vw,7rem)] lg:grid-cols-[1.2fr_1fr]" data-id="element-917" data-mp="3iohb58ltqahx">
        <Reveal data-id="element-918" data-mp="3nn2cxcgq528h">
          <h2 className="t-h2" data-id="element-919" data-mp="3nmitfrbfe5sc">What's included</h2>
          <ul className="mt-10 grid gap-x-8 sm:grid-cols-2" data-id="element-920" data-mp="3rnci4mwozoma">
            {service.included.map(item => <li key={item} className="flex min-h-[60px] items-center gap-3 border-b border-line text-[clamp(1rem,0.95rem+0.25vw,1.25rem)]" data-id="element-921" data-mp="9gha6krd4dr">
                <CheckIcon className="h-5 w-5 shrink-0 text-accent-soft" aria-hidden data-id="element-922" data-mp="581l0fz7y66y" />{item}
              </li>)}
          </ul>
        </Reveal>
        <Reveal delay={0.05} data-id="element-923" data-mp="f67imldzsq60">
          <h2 className="t-h2" data-id="element-924" data-mp="f6z2x02531gz">Who it's for</h2>
          <ul className="mt-10 grid gap-2" data-id="element-925" data-mp="j7sqfsbo6e9c">
            {service.whoFor.map(w => <li key={w} className="t-lead text-fg-2" data-id="element-926" data-mp="2gxu3e57ajou1">{w}</li>)}
          </ul>
          <h3 className="t-eyebrow mt-14 text-muted" data-id="element-927" data-mp="2h06v3a2updka">Sample page types</h3>
          <ul className="mt-5 flex flex-wrap gap-2" data-id="element-928" data-mp="2kzpeu2ixrilj">
            {service.pageTypes.map(p => <li key={p} className="rounded-full border border-line-strong px-4 py-2 text-[15px] text-fg-2" data-id="element-929" data-mp="1pnfu8dyvo0xi">{p}</li>)}
          </ul>
        </Reveal>
      </section>

      <section className="border-y border-line bg-ink-2" data-id="element-930" data-mp="1zm3zec5rkljs">
        <div className="shell section" data-id="element-931" data-mp="1uolnqe0d3s83">
          <SectionHeading eyebrow="Features" title="Built around how your customers act." data-id="element-932" data-mp="1upnctme7qf8t" />
          <ul className="grid gap-px overflow-hidden rounded-[clamp(1rem,1.2vw,1.5rem)] border border-line bg-line sm:grid-cols-2 2xl:grid-cols-4" data-id="element-933" data-mp="16zsyf0e8wimg">
            {service.features.map((f, i) => <Reveal as="li" key={f.title} delay={i * 0.04} className="bg-ink-2 p-[clamp(1.5rem,2.2vw,2.75rem)]" data-id="element-934" data-mp="1gpmae2iauzr8" data-mpi={i}>
                <div id={f.id} className="scroll-mt-28" data-id="element-935" data-mp="1bw9npxx6l314">
                  <h3 className="t-h3" data-id="element-936" data-mp="o80492nfwrd5">{f.title}</h3>
                  <p className="t-body mt-3 text-muted" data-id="element-937" data-mp="o6fd7n0c3qk1">{f.text}</p>
                </div>
              </Reveal>)}
          </ul>
        </div>
      </section>

      <section className="shell section" data-id="element-938" data-mp="2uvhbmajmwfys">
        <SectionHeading eyebrow="Process" title="How it comes together." data-id="element-939" data-mp="2uufov8t9a518" />
        <ProcessTimeline data-id="element-940" data-mp="2zotojneyaybp" />
      </section>

      <section className="shell section grid gap-10 border-t border-line lg:grid-cols-[0.8fr_1.2fr]" data-id="element-941" data-mp="2lvubrt6jzi7u">
        <SectionHeading eyebrow="FAQ" title={`${service.name} questions.`} data-id="element-942" data-mp="2px62k215y9hm" />
        <FaqAccordion items={service.faqs} idPrefix={`faq-${service.slug}`} data-id="element-943" data-mp="2pvcqu8bp0z2r" />
      </section>

      <CtaBand source={`service_${service.slug}`} data-id="element-944" data-mp="3dkxdp2aywghb" />
    </>;
}